/* Local preview: keep Shopify matching intact; skip empty result pages safely. */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root && root.document) api.mount(root);
})(typeof window === "undefined" ? null : window, function () {
  "use strict";

  function pageUrl(value, base) {
    const origin = new URL(base);
    const result = new URL(value, origin);
    const path = value => value.replace(/\/$/, "");
    if (result.origin !== origin.origin || path(result.pathname) !== path(origin.pathname)) {
      throw new Error("Unexpected results address");
    }
    // Shopify pagination may omit these theme-preview parameters. Keep the
    // selected unpublished theme during probes and the eventual navigation.
    for (const key of ["preview_theme_id", "_fd", "pb"]) {
      if (origin.searchParams.has(key)) result.searchParams.set(key, origin.searchParams.get(key));
    }
    result.searchParams.delete("section_id");
    const parameters = url => Array.from(url.searchParams.entries())
      .filter(([key]) => key !== "page").sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    const expected = new URL(origin);
    expected.searchParams.delete("section_id");
    if (JSON.stringify(parameters(result)) !== JSON.stringify(parameters(expected))) {
      throw new Error("Results filters changed");
    }
    const page = result.searchParams.get("page");
    if (!page || !/^[1-9]\d*$/.test(page) || !Number.isSafeInteger(Number(page))) {
      throw new Error("Invalid results page");
    }
    return result;
  }

  async function scan(next, options) {
    const { base, loadPage, signal } = options;
    const limit = options.limit === undefined ? 5 : options.limit;
    if (!Number.isInteger(limit) || limit < 1 || limit > 5) throw new Error("Invalid scan limit");
    let candidate = next;
    let previous = Number(new URL(base).searchParams.get("page") || 1);
    const seen = new Set();
    for (let count = 0; count < limit; count++) {
      if (signal && signal.aborted) throw new Error("Cancelled");
      const target = pageUrl(candidate, base);
      const page = Number(target.searchParams.get("page"));
      if (page <= previous || seen.has(target.href)) throw new Error("Results page did not advance");
      seen.add(target.href);
      const metadata = await loadPage(target, signal);
      if (signal && signal.aborted) throw new Error("Cancelled");
      if (!metadata || !Number.isSafeInteger(metadata.visible) || metadata.visible < 0 ||
          metadata.page !== page || typeof metadata.section !== "string") {
        throw new Error("Invalid results response");
      }
      if (metadata.visible > 0) return { type: "found", url: target.href };
      if (metadata.next === null || metadata.next === "") return { type: "end" };
      if (typeof metadata.next !== "string") throw new Error("Invalid next page");
      const pending = pageUrl(metadata.next, base);
      if (Number(pending.searchParams.get("page")) <= page) throw new Error("Results page did not advance");
      candidate = pending.href;
      previous = page;
    }
    return { type: "more", url: candidate };
  }

  function mount(win) {
    const doc = win.document;
    const active = new Set();
    function cancel() {
      for (const controller of active) controller.abort();
    }
    doc.addEventListener("theme:loading:start", cancel);
    win.addEventListener("pagehide", cancel);
    doc.addEventListener("click", async event => {
      const link = event.target.closest && event.target.closest("[data-kgc-next]");
      if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const wrapper = link.closest("[data-kgc-discovery]");
      if (!wrapper) return;
      event.preventDefault();
      if (link.getAttribute("aria-disabled") === "true") return;
      const status = wrapper.querySelector("[data-kgc-discovery-status]");
      const controller = new AbortController();
      const base = win.location.href;
      const originalHref = link.href;
      let timer;
      active.add(controller);
      link.setAttribute("aria-disabled", "true");
      status.textContent = "Looking for more products…";
      try {
        const current = JSON.parse(wrapper.querySelector("[data-kgc-discovery-page]").textContent);
        if (typeof current.section !== "string" || !current.section) throw new Error("Missing section");
        const result = await scan(originalHref, {
          base, signal: controller.signal,
          loadPage: async url => {
            const request = new URL(url);
            request.searchParams.set("section_id", current.section);
            timer = win.setTimeout(() => controller.abort(), 12000);
            try {
              const response = await win.fetch(request.href, {
                method: "GET", credentials: "same-origin", signal: controller.signal,
              });
              if (!response.ok) throw new Error("Could not load results");
              const parsed = new win.DOMParser().parseFromString(await response.text(), "text/html");
              const section = Array.from(parsed.querySelectorAll("[data-section-id]"))
                .find(element => element.getAttribute("data-section-id") === current.section);
              const node = section && section.querySelector("[data-kgc-discovery-page]");
              if (!node) throw new Error("Missing results response");
              const metadata = JSON.parse(node.textContent);
              if (metadata.section !== current.section) throw new Error("Wrong results section");
              return metadata;
            } finally {
              win.clearTimeout(timer);
            }
          },
        });
        if (controller.signal.aborted || !wrapper.isConnected || win.location.href !== base) return;
        if (result.type === "found") win.location.assign(result.url);
        else if (result.type === "more") {
          link.href = result.url;
          link.textContent = "Continue checking results";
          status.textContent = "More results remain to check. Select Continue to keep looking.";
        } else {
          link.hidden = true;
          status.textContent = "No further products to display.";
        }
      } catch (error) {
        if (wrapper.isConnected && win.location.href === base) {
          status.textContent = "Could not check more results. Select Next results to try again.";
          link.href = originalHref;
        }
      } finally {
        win.clearTimeout(timer);
        link.removeAttribute("aria-disabled");
        active.delete(controller);
      }
    });
  }
  return { pageUrl, scan, mount };
});
